import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AlertCircle, CheckCircle2, Gavel } from 'lucide-react';
import { Button } from '../ui/Button';
import type { ApiError } from '../../lib/api';
import {
  sesionApi,
  sesionKeys,
  resolverCaptura,
  type ActividadDetalle,
} from '../../lib/sesion';

/**
 * SUPERVOTO DEL DECISOR (Sprint CCJ) — decisión del owner: el voto de calidad
 * del decisor DEBE quedar en el event log y en el artefacto (trazabilidad del
 * acta, no opcional). Por eso se registra como un aporte más (C1: INSERT-only,
 * tipo 'voto' con payload.supervoto=true) por el endpoint genérico: entra al
 * hilo cronológico, al snapshot C6 y a la lista de la Cabina con su badge.
 *
 * Visible SOLO cuando la actividad lo exige (config.supervoto), está
 * abierta/pausada y quien mira puede conducir. Sin captura de voto resuelta
 * no hay opciones que ofrecer: no se muestra nada.
 */
export function Supervoto({
  actividad,
  puedeConducir,
}: {
  actividad: ActividadDetalle;
  /** Mismo gate grueso que el resto de la Cabina; el fino lo pone el backend. */
  puedeConducir: boolean;
}) {
  const qc = useQueryClient();
  const [opcion, setOpcion] = useState('');
  const [registrado, setRegistrado] = useState(false);

  const mut = useMutation({
    mutationFn: (op: string) =>
      sesionApi.postAporte(actividad.id, {
        tipo: 'voto',
        payload: { opcion: op, supervoto: true },
      }),
    onSuccess: () => {
      setRegistrado(true);
      void qc.invalidateQueries({ queryKey: sesionKeys.aportes(actividad.id) });
    },
  });

  const enCurso = actividad.estado === 'abierta' || actividad.estado === 'pausada';
  if (!actividad.supervoto || !enCurso || !puedeConducir) return null;

  // Opciones RESUELTAS de la captura de voto (el backend ya inyectó las del
  // artefacto fuente cuando el manifiesto declara opciones_desde).
  const resuelta = resolverCaptura(actividad.herramienta);
  if (!resuelta.soportada || resuelta.captura.tipo !== 'voto') return null;
  const opciones = resuelta.captura.opciones;

  const error = (mut.error as ApiError | null)?.detail ?? null;

  return (
    <section className="rounded-md border border-border-strong bg-surface-raised px-4 py-3">
      <p className="flex items-center gap-2 font-display text-sm font-semibold uppercase tracking-wide text-content">
        <Gavel className="h-4 w-4 shrink-0 text-primary" aria-hidden="true" />
        Supervoto del decisor
      </p>
      <p className="mt-1 font-sans text-xs text-content-muted">
        Queda registrado en el acta como voto del decisor, citable en la propuesta.
      </p>

      <div className="mt-2 flex flex-wrap items-center gap-2">
        <select
          value={opcion}
          onChange={(e) => setOpcion(e.target.value)}
          aria-label="Opción elegida por el decisor"
          className="min-h-touch min-w-0 flex-1 rounded-md border border-border-strong bg-surface-sunken px-3 font-sans text-sm text-content focus-visible:outline-none focus-visible:shadow-focus sm:max-w-xs sm:flex-none"
        >
          <option value="" disabled>
            Elegí la opción del decisor…
          </option>
          {opciones.map((op) => (
            <option key={op.valor} value={op.valor}>
              {op.etiqueta}
            </option>
          ))}
        </select>
        <Button
          variant="secondary"
          size="sm"
          disabled={!opcion}
          loading={mut.isPending}
          onClick={() => mut.mutate(opcion)}
          leadingIcon={<Gavel className="h-4 w-4" aria-hidden="true" />}
        >
          Registrar supervoto del decisor
        </Button>
      </div>

      {registrado && !mut.isPending && (
        <p className="mt-2 flex items-center gap-1.5 font-sans text-sm text-success">
          <CheckCircle2 className="h-4 w-4 shrink-0" aria-hidden="true" />
          Supervoto registrado en el acta
        </p>
      )}
      {error && (
        <p className="mt-2 flex items-center gap-1.5 font-sans text-sm text-error">
          <AlertCircle className="h-4 w-4 shrink-0" aria-hidden="true" />
          {error}
        </p>
      )}
    </section>
  );
}
